import React, { useEffect, useState } from 'react';
import { required, isProbablyEmail, hasLength } from 'redux-freeform';
import {
  ButtonWithAction,
  Cluster,
  FormContainer,
  FormInput,
  Text,
  util,
  Box,
  Stack,
  Paragraph,
  Checkbox,
  constants,
} from '@thecb/components';

import { formatCurrencyInput } from '../../../utils/helpers';

import {
  paylinkReducer,
  paylinkMapStateToProps,
  paylinkMapDispatchToProps,
} from '../CheckoutForm.state';
import { useDispatch, useSelector } from 'react-redux';
import { initiatePayLink } from '../Checkout.state';
import {
  PAYLINK,
  EMAIL_ADDRESS_FIELD_NAME,
  PHONE_NUMBER_FIELD_NAME,
} from '../../../utils/constants';
import { useLocation, useNavigate } from 'react-router-dom';
import { paymentAmountErrors } from './shared-constants';

const emailPayLinkErrors = {
  [required.error]: 'Email address required',
  [isProbablyEmail.error]: 'Not a valid email address',
};
const textPayLinkErrors = {
  [required.error]: 'Phone number required',
  [hasLength.error]: '10 digit phone number required',
};

let PayLinkCheckoutForm = ({
  actions,
  fields,
  forms,
  processBtnText,
  order,
}) => {
  const dispatch = useDispatch();
  const [errors, handleErrors] = useState(false);
  const { isLoading } = useSelector((state) => state.checkout);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (order?.total) {
      actions.fields.amount.set(formatCurrencyInput(order?.total));
    }
  }, [order?.total, actions.fields?.amount]);

  const isPayingFullAmount =
    Number(fields.amount.rawValue) === Number(order?.total) &&
    Number(order?.remaining_balance) === Number(order?.total);

  const resetFieldValueOnSelect = (field) => {
    if (field && fields[field]?.rawValue) {
      actions.fields[field].set('');
    }
  };

  const handleEmailPayLinkCheckboxClick = () => {
    actions.fields.emailPayLinkCheckbox.set(!currentEmailPayLinkBoolean);
    resetFieldValueOnSelect(EMAIL_ADDRESS_FIELD_NAME);
  };
  const handleTextPayLinkCheckboxClick = () => {
    actions.fields.textPayLinkCheckbox.set(!currentTextPayLinkBoolean);
    resetFieldValueOnSelect(PHONE_NUMBER_FIELD_NAME);
  };

  const currentEmailPayLinkBoolean = Boolean(
    fields.emailPayLinkCheckbox.rawValue,
  );
  const currentTextPayLinkBoolean = Boolean(
    fields.textPayLinkCheckbox.rawValue,
  );

  const disableSendPaymentLinkButton =
    !isPayingFullAmount ||
    isLoading ||
    (!fields.emailAddress.rawValue && !fields.phoneNumber.rawValue) ||
    fields.emailAddress.errors.length ||
    fields.phoneNumber.errors.length ||
    fields.amount.errors.length;

  const handleSendPayLinkClick = util.general.generateClickHandler(
    forms.paylinkCheckoutForm,
    () => handleErrors(true),
    () =>
      dispatch(
        initiatePayLink({
          amount_given: Number(fields.amount.rawValue),
          payment_tender_type: PAYLINK,
          email: fields.emailAddress.rawValue,
          phone: fields.phoneNumber.rawValue,
          invoice_types: [
            ...(fields.emailAddress.rawValue ? ['email'] : []),
            ...(fields.phoneNumber.rawValue ? ['sms'] : []),
          ],
          navigate,
          location,
        }),
      ),
  );

  return (
    <FormContainer>
      <FormInput
        id="paylink-payment-amt"
        type="number"
        labelTextWhenNoError="Payment Amount"
        errorMessages={paymentAmountErrors}
        field={fields.amount}
        errors={fields.errors}
        fieldActions={actions.fields.amount}
        disabled
        labelTextVariant="p"
        errorTextVariant="p"
      />
      <Stack width="50%" extraStyles={`flex-grow: 1;`}>
        <Paragraph
          id="enter-email-or-phone-number-explanation"
          role="note"
          extraStyles={`max-width: 32rem`}
        >
          Enter an email address or phone number to send this order as a payment
          link to the customer.
        </Paragraph>
        <Checkbox
          id="email-link"
          title="Email Link"
          name="Email Link"
          checked={currentEmailPayLinkBoolean}
          onChange={handleEmailPayLinkCheckboxClick}
        />
        {currentEmailPayLinkBoolean && (
          <FormInput
            id="email-address-input"
            labelTextWhenNoError="Email Address"
            errorMessages={emailPayLinkErrors}
            field={fields.emailAddress}
            errors={fields.errors}
            showErrors={errors}
            fieldActions={actions.fields.emailAddress}
            labelTextVariant="p"
            errorTextVariant="p"
          />
        )}
        <Checkbox
          id="text-link"
          title="Text Link"
          name="Text Link"
          checked={currentTextPayLinkBoolean}
          onChange={handleTextPayLinkCheckboxClick}
        />
        {currentTextPayLinkBoolean && (
          <FormInput
            id="phone-number-input"
            labelTextWhenNoError="Phone Number"
            errorMessages={textPayLinkErrors}
            field={fields.phoneNumber}
            errors={fields.errors}
            showErrors={errors}
            fieldActions={actions.fields.phoneNumber}
            labelTextVariant="p"
            errorTextVariant="p"
          />
        )}
      </Stack>
      <Box padding="16px 0 0">
        <Cluster>
          <ButtonWithAction
            text={'Back to Cart'}
            action={() => navigate('/pos/cart')}
            variant="secondary"
            dataQa="view-cart"
            extraStyles="margin-left: 0"
            textExtraStyles="font-size: 1rem;"
          />
          <ButtonWithAction
            id="paylink-payment-btn"
            aria-describedby="must-pay-in-full-explanation enter-email-or-phone-number-explanation"
            disabled={disableSendPaymentLinkButton}
            isLoading={isLoading}
            text={processBtnText}
            action={handleSendPayLinkClick}
            textExtraStyles="font-size: 1rem;"
          />
        </Cluster>
      </Box>
      {!isPayingFullAmount && (
        <Box padding="8px 0px">
          <Text
            id="must-pay-in-full-explanation"
            role="alert"
            color={constants.colors.THUNDERBIRD_RED}
          >
            PayLink does not support split-tender transactions. Please void any
            completed payments and pay in full via PayLink.
          </Text>
        </Box>
      )}
    </FormContainer>
  );
};

PayLinkCheckoutForm.reducer = paylinkReducer;
PayLinkCheckoutForm.mapStateToProps = paylinkMapStateToProps;
PayLinkCheckoutForm.mapDispatchToProps = paylinkMapDispatchToProps;

export default PayLinkCheckoutForm;
